import type { ColumnDef } from '@tanstack/react-table';

import { type GetCreditsQuery, CreditSource, CreditType } from 'data/__generated__';
import { dateColumn, multiSelectColumn, numberColumn, textColumn } from 'components/DataTable';

import { CREDIT_SOURCE_LABELS, CREDIT_TYPE_LABELS } from './index';

export type CreditRow = GetCreditsQuery['credits']['credits'][number];

export type CreditsFilterInput = {
    userId?: string;
    types?: CreditType[];
    sources?: CreditSource[];
};

export const creditsColumns: ColumnDef<CreditRow, unknown>[] = [
    dateColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        defaultVisible: true,
    }),
    dateColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'updatedAt',
        header: 'Dernière mise à jour',
        defaultVisible: true,
    }),
    textColumn<CreditRow, CreditsFilterInput>({
        id: 'createdBy',
        accessorFn: (row) => row.createdBy?.profile?.fullname || '',
        header: 'Créé par',
        defaultVisible: true,
    }),
    multiSelectColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'type',
        header: 'Type',
        filterKey: 'types',
        defaultVisible: true,
        options: Object.values(CreditType).map((type) => ({
            value: type,
            label: CREDIT_TYPE_LABELS[type] || type,
        })),
        cell: ({ row }) => <span>{CREDIT_TYPE_LABELS[row.original.type] || row.original.type}</span>,
    }),
    multiSelectColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'source',
        header: 'Source',
        filterKey: 'sources',
        defaultVisible: true,
        options: Object.values(CreditSource).map((source) => ({
            value: source,
            label: CREDIT_SOURCE_LABELS[source],
        })),
        cell: ({ row }) => <span>{row.original.source ? CREDIT_SOURCE_LABELS[row.original.source] : ''}</span>,
    }),
    textColumn<CreditRow, CreditsFilterInput>({
        id: 'organizationName',
        accessorFn: (row) => row.organizationBatched?.name || '',
        header: "Lot pour l'organisation",
        defaultVisible: true,
    }),
    textColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'name',
        header: 'Nom du crédit',
        defaultVisible: true,
    }),
    textColumn<CreditRow, CreditsFilterInput>({
        id: 'owner',
        accessorFn: (row) => row.owner?.profile?.fullname || '',
        header: 'Utilisateur',
        filterKey: 'userId',
        defaultVisible: true,
    }),
    numberColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'initialAmount',
        header: 'Montant initial',
        defaultVisible: true,
    }),
    numberColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'currentAmount',
        header: 'Montant restant',
        defaultVisible: true,
    }),
    dateColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'refundedAt',
        header: 'Remboursé le',
        defaultVisible: true,
    }),
    dateColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'startDate',
        header: 'Date de début de validité',
        defaultVisible: true,
    }),
    dateColumn<CreditRow, CreditsFilterInput>({
        accessorKey: 'expirationDate',
        header: "Date d'expiration",
        defaultVisible: true,
    }),
];
